/**
 * Label Generator V2 - Thermal Printer Component
 * Version: 2.0.0
 * Last Updated: 2025-12-30
 *
 * Description:
 *   Generate thermal printer labels with ZPL/ESC-POS commands
 *   - File upload with preview
 *   - Support for single and 2-up formats
 *   - ZPL (Zebra) and ESC/POS printer support
 *   - Direct printer command generation
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from '@mui/material';
import {
    Upload as UploadIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';

interface PreviewRow {
    id: number;
    'order #': string;
    name: string;
}

interface PreviewData {
    total_entries: number;
    duplicates_removed: number;
    valid_labels: number;
    preview_data: PreviewRow[];
    columns: string[];
    all_data: Array<{ 'order #': string; name: string }>;
}

export default function LabelGeneratorV2() {
    const { enqueueSnackbar } = useSnackbar();
    const gridRef = useGridApiRef();

    // State
    const [file, setFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Configuration
    const [printerType, setPrinterType] = useState<'zpl' | 'escpos'>('zpl');
    const [formatType, setFormatType] = useState<'single' | '2up'>('2up');
    const [dpi, setDpi] = useState<203 | 300>(203);

    // Drag and drop handlers
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            validateAndLoadFile(droppedFile);
        }
    };

    const validateAndLoadFile = async (uploadedFile: File) => {
        // Validate file type
        const validTypes = ['.xlsx', '.xls', '.csv'];
        const fileExt = uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.')).toLowerCase();

        if (!validTypes.includes(fileExt)) {
            enqueueSnackbar('Invalid file type. Please upload Excel or CSV file.', { variant: 'error' });
            return;
        }

        // Validate file size (20MB)
        if (uploadedFile.size > 20 * 1024 * 1024) {
            enqueueSnackbar('File too large. Maximum size is 20 MB.', { variant: 'error' });
            return;
        }

        setFile(uploadedFile);
        setLoading(true);

        try {
            // Call label generator v2 preview API
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const response = await fetch('/api/v1/label-generator-v2/preview', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: formData
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to preview');
            }
            const preview = await response.json();
            setPreviewData(preview);
            enqueueSnackbar('File loaded successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.message || 'Failed to load file', { variant: 'error' });
            setFile(null);
            setPreviewData(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload (click)
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            validateAndLoadFile(uploadedFile);
        }
    };

    // Handle label generation
    const handleGenerate = async () => {
        if (!previewData) return;

        setGenerating(true);
        try {
            const response = await fetch('/api/v1/label-generator-v2/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({
                    data: previewData.all_data,
                    printer_type: printerType,
                    format_type: formatType,
                    dpi: dpi
                })
            });

            if (!response.ok) throw new Error('Failed to generate');

            const blob = await response.blob();

            // Download the file
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('content-disposition');
            let filename = printerType === 'zpl' ? 'labels.zpl' : 'labels.bin';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`Labels generated! Send ${filename} to your thermal printer.`, { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar('Failed to generate labels', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        { field: 'id', headerName: '#', width: 70 },
        { field: 'order #', headerName: 'Order #', width: 150 },
        { field: 'name', headerName: 'Customer Name', width: 300 },
    ];

    // Prepare rows for DataGrid
    const rows = previewData?.preview_data?.map((item, idx) => ({
        ...item,
        id: idx + 1,
    })) || [];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    🏷️ Label Generator V2
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Generate thermal printer labels with ZPL/ESC-POS commands
                </Typography>
            </Box>

            {/* Configuration */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    ⚙️ Printer Configuration
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Printer Type</InputLabel>
                        <Select
                            value={printerType}
                            label="Printer Type"
                            onChange={(e) => setPrinterType(e.target.value as 'zpl' | 'escpos')}
                        >
                            <MenuItem value="zpl">ZPL (Zebra Printers)</MenuItem>
                            <MenuItem value="escpos">ESC/POS (Generic Thermal)</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Label Format</InputLabel>
                        <Select
                            value={formatType}
                            label="Label Format"
                            onChange={(e) => setFormatType(e.target.value as 'single' | '2up')}
                        >
                            <MenuItem value="single">Single (50×25mm)</MenuItem>
                            <MenuItem value="2up">2-Up (100×25mm)</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={printerType !== 'zpl'}>
                        <InputLabel>DPI (ZPL only)</InputLabel>
                        <Select
                            value={dpi}
                            label="DPI (ZPL only)"
                            onChange={(e) => setDpi(e.target.value as 203 | 300)}
                        >
                            <MenuItem value={203}>203 DPI (Standard)</MenuItem>
                            <MenuItem value={300}>300 DPI (High-Res)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>2-Up Format:</strong> Prints two 50×25mm labels side-by-side on 100×25mm paper.
                    Recommended for efficient label usage!
                </Alert>
            </Paper>

            {/* File Upload */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
                    bgcolor: isDragging ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s ease'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Typography variant="h6" gutterBottom>
                    📤 Upload File
                </Typography>
                <Box sx={{ mt: 2, textAlign: 'center', py: 3 }}>
                    <input
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        id="file-upload"
                        type="file"
                        onChange={handleFileUpload}
                    />
                    <label htmlFor="file-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            disabled={loading}
                            size="large"
                        >
                            {loading ? 'Loading...' : 'Choose File or Drag & Drop Here'}
                        </Button>
                    </label>
                    {file && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" />
                            <Typography variant="body2">{file.name}</Typography>
                        </Box>
                    )}
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                    File must contain <strong>order #</strong> and <strong>name</strong> columns
                </Alert>
            </Paper>

            {/* Preview Section */}
            {previewData && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        📊 Summary
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Entries
                            </Typography>
                            <Typography variant="h5">{previewData.total_entries}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Duplicates Removed
                            </Typography>
                            <Typography variant="h5">{previewData.duplicates_removed}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Valid Labels
                            </Typography>
                            <Typography variant="h5" color="primary">
                                {previewData.valid_labels}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Format
                            </Typography>
                            <Typography variant="h5">
                                {formatType === '2up' ? (
                                    <Chip label="2-Up" color="success" size="small" />
                                ) : (
                                    <Chip label="Single" color="default" size="small" />
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    {formatType === '2up' && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            ✅ Using 2-Up format: {Math.ceil(previewData.valid_labels / 2)} pages will be printed
                            (2 labels per page)
                        </Alert>
                    )}

                    {/* Visual Label Preview */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="h6">
                                🖼️ Label Preview
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowPreview(!showPreview)}
                            >
                                {showPreview ? 'Hide Preview' : 'Show Preview'}
                            </Button>
                        </Box>

                        {showPreview && (
                            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                    Visual mockup of how labels will appear (not to exact scale)
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    maxHeight: 400,
                                    overflowY: 'auto'
                                }}>
                                    {formatType === '2up' ? (
                                        // 2-Up Preview
                                        <>
                                            {Array.from({ length: Math.ceil(previewData.valid_labels / 2) }).map((_, pageIdx) => {
                                                const label1 = previewData.all_data[pageIdx * 2];
                                                const label2 = previewData.all_data[pageIdx * 2 + 1];

                                                return (
                                                    <Box key={pageIdx} sx={{ display: 'flex', gap: 1 }}>
                                                        {/* Label 1 */}
                                                        <Box sx={{
                                                            width: '200px',
                                                            height: '100px',
                                                            border: '2px solid #333',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            bgcolor: 'white',
                                                            p: 1
                                                        }}>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                #{label1['order #']}
                                                            </Typography>
                                                            <Box sx={{ width: '80%', borderTop: '2px solid #333', my: 0.5 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                                                {label1.name.toUpperCase()}
                                                            </Typography>
                                                        </Box>

                                                        {/* Label 2 */}
                                                        {label2 && (
                                                            <Box sx={{
                                                                width: '200px',
                                                                height: '100px',
                                                                border: '2px solid #333',
                                                                borderRadius: 1,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                bgcolor: 'white',
                                                                p: 1
                                                            }}>
                                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                    #{label2['order #']}
                                                                </Typography>
                                                                <Box sx={{ width: '80%', borderTop: '2px solid #333', my: 0.5 }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                                                    {label2.name.toUpperCase()}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        // Single Label Preview
                                        <>
                                            {previewData.all_data.slice(0, 10).map((label, idx) => (
                                                <Box key={idx} sx={{
                                                    width: '200px',
                                                    height: '100px',
                                                    border: '2px solid #333',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    bgcolor: 'white',
                                                    p: 1
                                                }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        #{label['order #']}
                                                    </Typography>
                                                    <Box sx={{ width: '80%', borderTop: '2px solid #333', my: 0.5 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                                        {label.name.toUpperCase()}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            {previewData.valid_labels > 10 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Showing first 10 of {previewData.valid_labels} labels
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        )}
                    </Box>


                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        👀 Data Preview
                    </Typography>
                    <Box
                        className={isFullscreen ? 'datagrid-fullscreen' : ''}
                        sx={{
                            height: isFullscreen ? '100vh' : 400,
                            width: '100%',
                            transition: 'height 0.3s ease',
                        }}
                    >
                        <DataGrid
                            apiRef={gridRef}
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 20 } },
                            }}
                            pageSizeOptions={[10, 20, 50]}
                            disableRowSelectionOnClick
                            slots={{
                                toolbar: () => (
                                    <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            size="small"
                                        >
                                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                        </Button>
                                    </Box>
                                ),
                            }}
                            sx={{
                                border: '1px solid #e0e0e0',
                                height: '100%',
                                '& .MuiDataGrid-cell': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid #e0e0e0',
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                            }}
                        />
                    </Box>
                    {previewData.preview_data.length < previewData.valid_labels && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing first {previewData.preview_data.length} of {previewData.valid_labels} labels
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={generating ? <CircularProgress size={20} /> : <PrintIcon />}
                        onClick={handleGenerate}
                        disabled={generating}
                        fullWidth
                        sx={{ mt: 3 }}
                    >
                        {generating ? 'Generating...' : `🚀 Generate ${previewData.valid_labels} Labels (${printerType.toUpperCase()})`}
                    </Button>
                </Paper>
            )}

            {/* Help Section */}
            {!previewData && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>📖 How to Use</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" component="div">
                            <strong>Instructions:</strong>
                            <ol>
                                <li>
                                    <strong>Configure:</strong> Select printer type (ZPL/ESC-POS) and format (Single/2-Up)
                                </li>
                                <li>
                                    <strong>Upload:</strong> Excel or CSV file with <code>order #</code> and <code>name</code> columns
                                </li>
                                <li>
                                    <strong>Preview:</strong> Review data and verify label count
                                </li>
                                <li>
                                    <strong>Generate:</strong> Download printer command file (.zpl or .bin)
                                </li>
                                <li>
                                    <strong>Print:</strong> Send file to your thermal printer
                                </li>
                            </ol>

                            <strong>Printer Types:</strong>
                            <ul>
                                <li><strong>ZPL:</strong> Zebra thermal printers (generates .zpl file)</li>
                                <li><strong>ESC/POS:</strong> Generic thermal printers (generates .bin file)</li>
                            </ul>

                            <strong>Label Formats:</strong>
                            <ul>
                                <li><strong>Single:</strong> One 50×25mm label per print</li>
                                <li><strong>2-Up:</strong> Two 50×25mm labels side-by-side (100×25mm paper)</li>
                            </ul>

                            <strong>How to Print:</strong>
                            <ul>
                                <li><strong>USB:</strong> <code>cat labels.zpl &gt; /dev/usb/lp0</code></li>
                                <li><strong>Network:</strong> <code>cat labels.zpl | nc PRINTER_IP 9100</code></li>
                                <li><strong>Zebra Utilities:</strong> Tools → Send File → Select .zpl file</li>
                            </ul>
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
}
